import type { Product } from "schemas/generated/product";
import type { StoreProductSummary } from "schemas/product";

import React from "react";

import { skipToken } from "@tanstack/react-query";
import {
  selectProductSummaries,
  setProductSummaries,
} from "@v1/reducers/commerce-reducer";
import {
  selectDraftElementTemplateProducts,
  selectIsShopifyIntegrationEnabled,
} from "@v1/reducers/core-reducer";
import { selectTemplateEditorProduct } from "@v1/reducers/template-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import { getProductImage } from "@v1/utils/product";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { productRouter } from "@v2/trpc/routes/product";
import uniq from "lodash-es/uniq";
import {
  fakeProducts,
  loadingProduct,
} from "replo-runtime/store/utils/fakeProducts";
import { filterNulls } from "replo-utils/lib/array";
import { isEmpty } from "replo-utils/lib/misc";

function mapProductToStoreProductSummary(
  product: Product,
): StoreProductSummary | null {
  if (!product.externalId) {
    return null;
  }

  const externalId = Number.parseInt(product.externalId, 10);
  if (Number.isNaN(externalId)) {
    return null;
  }

  const firstVariant = product.variants[0];
  const defaultVariantId = firstVariant?.externalId
    ? Number.parseInt(firstVariant.externalId, 10)
    : null;

  if (!defaultVariantId) {
    return null;
  }

  return {
    id: externalId,
    title: product.title,
    featuredImage: getProductImage(product),
    defaultVariantId: defaultVariantId,
  };
}

export const productSummaryRequestLimits = {
  infinite: 20,
  default: 100,
  maximum: 250,
};

type ProductSummaryDataMap = {
  cachedProductSummaries: StoreProductSummary[];
  productIdsToBeRequested: number[];
};

const useCachedProductSummaries = ({
  ids,
  firstN,
}: {
  ids?: number[];
  firstN?: number;
}) => {
  const productSummariesFromStore: Record<string, StoreProductSummary> =
    useEditorSelector(selectProductSummaries);

  // NOTE (Chance 2023-06-28): This is a quick and dirty workaround to avoid the
  // need to memoize the incoming array of product ids unless one of its values
  // actually changes.
  let idsStringified: string | null = null;
  try {
    if (ids) {
      idsStringified = JSON.stringify(ids);
    }
  } catch {}

  const productSummaryDataMap: ProductSummaryDataMap = React.useMemo(() => {
    let ids: number[] = idsStringified ? JSON.parse(idsStringified) : [];
    // NOTE (Matt 2024-11-25): In some circumstances, this fn is called without a list
    // of ids but with a number of product summaries to be requested. In that case,
    // we just grab the first of that quantity from the store.
    if (ids.length < 1 && firstN) {
      ids = Object.keys(productSummariesFromStore)
        .slice(0, firstN)
        .map((id) => Number(id));
    }
    const uniqueIds = uniq(ids);
    return {
      cachedProductSummaries: filterNulls(
        uniqueIds.map((id) => productSummariesFromStore[id]),
      ),
      productIdsToBeRequested: uniqueIds.filter(
        (id) => !Object.keys(productSummariesFromStore).includes(String(id)),
      ),
    };
  }, [idsStringified, firstN, productSummariesFromStore]);

  return productSummaryDataMap;
};

function useFetchStoreProductSummaries({
  ids,
  pageSize,
  forceSkip,
}: {
  ids?: number[];
  pageSize?: number;
  forceSkip?: boolean;
}) {
  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const { cachedProductSummaries, productIdsToBeRequested } =
    useCachedProductSummaries({ ids, firstN: pageSize });
  const { dispatch } = useEditorStore();

  const shouldSkip =
    forceSkip ||
    (ids && productIdsToBeRequested.length === 0) ||
    !isShopifyIntegrationEnabled ||
    !projectId;

  const queryParams = shouldSkip
    ? skipToken
    : {
        projectId,
        limit: Math.min(
          pageSize ?? productSummaryRequestLimits.default,
          productSummaryRequestLimits.maximum,
        ),
        // Note (Noah, 2025-09-08): replo-hosted projects always pass write-through products sync,
        // since in order to create a v2 project users must have updated their Shopify scopes
        writeThroughProductsSync:
          project?.hostingType === "replo" ||
          isFeatureEnabled("write-through-products-sync"),
        filters: ids
          ? { externalIds: productIdsToBeRequested.map(String) }
          : { searchTerm: "" },
      };

  const { data, isLoading } = productRouter.list.useQuery(queryParams);

  const productSummariesFromQuery = React.useMemo(() => {
    if (!data?.products) {
      return [];
    }

    return filterNulls(data.products.map(mapProductToStoreProductSummary));
  }, [data?.products]);

  const allProductSummaries = React.useMemo(() => {
    return [...cachedProductSummaries, ...productSummariesFromQuery];
  }, [cachedProductSummaries, productSummariesFromQuery]);

  // Note (Kevin, 2025-06-20): Maintain Redux side effect for components that depend on product summary data store
  // This replicates RTK Query's automatic Redux updates on query success
  React.useEffect(() => {
    if (productSummariesFromQuery.length > 0) {
      dispatch(setProductSummaries(productSummariesFromQuery));
    }
  }, [productSummariesFromQuery, dispatch]);

  return {
    productsSummary: allProductSummaries,
    isLoading,
  };
}

const useFirstNProductsSummary = (nProducts: number) => {
  const { productsSummary, isLoading } = useFetchStoreProductSummaries({
    pageSize: nProducts,
  });
  return { productsSummary, isLoading };
};

export const useFirstNProductsSummaryOrPlaceholderProducts = (
  nProducts: number = productSummaryRequestLimits.default,
) => {
  const { productsSummary, isLoading } = useFirstNProductsSummary(nProducts);
  if (!isLoading && productsSummary && productsSummary.length === 0) {
    // NOTE (Jackson, 2025-01-28): Inserting the loading product first here
    // as this is the current fallback during any loading state
    // (page load, swapping template product w/ arrow handler, etc.)
    // I'm not certain if we ever care about the actualy placeholder products here,
    // maybe we can delete them and simply load the loading product instead?
    return {
      productsSummary: [
        {
          id: Number(loadingProduct.id),
          title: loadingProduct.title,
          featuredImage: loadingProduct.featured_image,
          defaultVariantId: loadingProduct.variants[0]?.id ?? 0,
        },
        ...fakeProducts.map((fakeProduct) => ({
          id: Number(fakeProduct.id),
          title: fakeProduct.title,
          featuredImage: fakeProduct.featured_image,
          defaultVariantId: fakeProduct.variants[0]?.id ?? 0,
        })),
      ],
      isLoading,
      isPlaceholderProducts: true,
    };
  }
  return {
    productsSummary,
    isLoading,
    isPlaceholderProducts: false,
  };
};

const useFetchSpecificProductsSummary = (
  ids: number[] | undefined,
  forceSkip: boolean = false,
) => {
  const { productsSummary, isLoading } = useFetchStoreProductSummaries({
    ids,
    forceSkip,
  });
  return { productsSummary, isLoading };
};

export const useTemplateEditorProductSummary = () => {
  const templateEditorProduct = useEditorSelector(selectTemplateEditorProduct);
  const { productsSummary: firstNProductsSummary } = useFirstNProductsSummary(
    productSummaryRequestLimits.default,
  );

  // Note (Evan, 2023-10-30): We check first in the fake products and the first n products, so that we only fire off
  // an additional request if we have to
  const placeholderProduct = fakeProducts.find((fakeProduct) => {
    return Number(fakeProduct.id) === Number(templateEditorProduct?.productId);
  });

  const cachedProduct =
    firstNProductsSummary &&
    firstNProductsSummary.find((existingProduct) => {
      return existingProduct.id === Number(templateEditorProduct?.productId);
    });

  const { productsSummary, isLoading } = useFetchSpecificProductsSummary(
    [templateEditorProduct ? Number(templateEditorProduct.productId) : 0],
    Boolean(!templateEditorProduct || placeholderProduct || cachedProduct),
  );

  if (!templateEditorProduct) {
    return {
      templateEditorProductSummary: null,
      isLoading: false,
    };
  }

  if (placeholderProduct) {
    return {
      templateEditorProductSummary: {
        title: placeholderProduct.title,
        id: Number(placeholderProduct.id),
        featuredImage: placeholderProduct.featured_image,
        defaultVariantId: placeholderProduct.variants[0]?.id ?? 0,
      },
      isLoading: false,
    };
  }

  if (cachedProduct) {
    return {
      templateEditorProductSummary: cachedProduct,
      isLoading: false,
    };
  }

  if (isLoading || !productsSummary) {
    return {
      templateEditorProductSummary: null,
      isLoading: true,
    };
  }

  if (!productsSummary[0]) {
    return {
      templateEditorProductSummary: null,
      isLoading: false,
    };
  }

  if (productsSummary[0].id !== Number(templateEditorProduct.productId)) {
    return {
      templateEditorProductSummary: null,
      isLoading: true,
    };
  }

  return {
    templateEditorProductSummary: productsSummary[0],
    isLoading: false,
  };
};

export const useAssignedProductsSummary = () => {
  const assignedProductIds = useEditorSelector(
    selectDraftElementTemplateProducts,
  );

  const { productsSummary, isLoading } = useFetchSpecificProductsSummary(
    assignedProductIds,
    isEmpty(assignedProductIds),
  );

  if (isEmpty(assignedProductIds) || isLoading || !productsSummary) {
    return { productsSummary: [], isLoading };
  }
  return {
    productsSummary,
    isLoading,
  };
};
